
    import Vue from "vue";
    import Component from "vue-class-component";
    import { StateChanger } from "vue-infinite-loading";
    import { Watch } from "vue-property-decorator";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import * as toastr from "toastr";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import ChangePasswordDialogue from "@/vue/components/ChangePasswordDialogue.vue";
    import UserDialogue from "@/vue/components/UserDialogue.vue";
    import { UserSearchParameters } from "@/model/UserSearchParameters";
    import { ILookupItem } from "@/model/LookupItem";
    import { User, IUser } from "@/model/User";
    import { UserRole, UserStatus } from "@/model/Enums";
    import { ISiteLookup } from "@/model/Site";
import { IClientLookup } from "@/model/Client";

    interface ISearchResponse {
        list: Array<IUser>;
        count: number;
    }

    @Component({
        components: { ApiButton, ConfirmDialogue, ChangePasswordDialogue, UserDialogue }
    })
    export default class Users extends Vue {

        async mounted(): Promise<void> {
            const loadClients = async () => { this.clientList = await ApiClient.get("/api/client/lookups"); }
            const loadSites = async () => { this.siteList = await ApiClient.get("/api/site/lookups"); }
            await Promise.all([
                loadClients(),
                loadSites()
            ]);
            // set filters for type of user (but see also FilerForUser on server!)
            const user = this.$store.state.signedInUser;
            if(user?.role == UserRole.Site) {
                this.searchParameters.siteID = user.siteID;
            }
            else if(user?.role == UserRole.Client && user.clientIDs.length === 1) {
                this.searchParameters.clientID = user.clientIDs[0];
            }
        }

        userHeaders = [
            { text: "Name", value: "name", sortable: false },
            { text: "Email", value: "email", sortable: false },
            { text: "Role", value: "role", sortable: false },
            { text: "Last Sign-In", value: "lastLoggedIn", sortable: false },
            { text: "Status", value: "status", sortable: false },
            { text: "Password Reset", value: "password", sortable: false }
        ]

        get canFilterClient(): boolean { return this.$store.state.signedInUser?.canFilterClient === true; }
        get canFilterSite(): boolean { return this.$store.state.signedInUser?.canFilterSite === true; }
        clientList: Array<IClientLookup> = [];
        get clientOptions(): Array<IClientLookup> { return Utils.clientOptionsFilter(this.clientList); }
        siteList: Array<ISiteLookup> = [];
        get siteOptions(): Array<ISiteLookup> { return Utils.siteOptionsFilter(this.siteList, this.searchParameters.clientID); }
        infiniteId: number = + new Date();
        searchParameters = new UserSearchParameters();
        resultCount: string = "-";
        userList: Array<IUser> = [];
        readonly user = new User();

        //
        // -- watchers
        //

        debouncedRefreshSearch = Utils.debounce(this.refreshSearch, 200);

        @Watch("searchParameters.searchText")
        onSearchTextChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.clientID")
        private onClientIdChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.siteID")
        private onSiteIdChanged(): void {
            this.debouncedRefreshSearch();
        }

        //
        // -- methods
        //

        roleText(user: User): string {
            const clientText = user.clientIDs.length == 0
                ? ""
                : this.clientList
                    .filter(i => user.clientIDs.indexOf(String(i.id)) > -1)
                    .map(i => i.description)
                    .join(", ");
                //: this.clientList.filter(l => l.id === user.clientID)[0]?.description ?? "- - -";
            const siteText = Utils.isEmptyId(user.siteID)
                ? ""
                : this.siteList.filter(l => l.id === user.siteID)[0]?.description ?? "- - -";

            if(user.role === UserRole.Client) {
                return `<b>Client${user.clientIDs.length > 1 ? "s" : ""}</b>: ${clientText} <b>${user.isUserAdmin ? "(user admin)" : ""}</b>`;
            }
            else if(user.role === UserRole.Site) {
                return `<b>Site</b>: ${siteText} <b>${user.isUserAdmin ? "(user admin)" : ""}</b>`;
            }
            else if(user.role === UserRole.SitePathways) {
                return `<b>Site</b> : ${siteText} (<b>pathways</b>: ${user.pathwayIDs.length})`;
            }
            else if(user.role === UserRole.SiteSponsors) {
                return `<b>Site</b>: ${siteText} (<b>sponsors</b>: ${user.sponsorRecipientIDs.length})`;
            }
            return UserRole[user.role];
        }

        canSendReminder(user: User): boolean {
            return !user.isDeleted && user.status !== UserStatus.EmailConfirmed;
        }

        canChangePassword(user: User): boolean {
            return !user.isDeleted && user.status == UserStatus.Active;
        }

        sendLinkText(user: User): string {
            return user.status < UserStatus.EmailConfirmed ? "Send another link" : "Send a link";
        }

        statusText(user: User): string {
            if (Utils.hasDateValue(user.deleted)) return "Archived";
            if (user.status === UserStatus.InitialEmailPending) return "Email pending";
            if (user.status === UserStatus.InitialEmailFailed) return "Email FAILED";
            if (user.status === UserStatus.InitialEmailSent) return "Email sent";
            if (user.status === UserStatus.EmailConfirmed) return "Email confirmed";
            if (user.status === UserStatus.Active) return "Active";
            return "Unknown";
        }

        editNew(): void {
            const dialog: UserDialogue = this.$refs.userDialogue as UserDialogue;
            dialog.add();
        }

        edit(user: IUser): void {
            const dialog: UserDialogue = this.$refs.userDialogue as UserDialogue;
            dialog.edit(user.id);
        }

        refreshSearch(): void {
            this.resultCount = "-";
            this.userList = [];
            this.searchParameters.pageNumber = 1;
            this.infiniteId += 1;
        }

        async infiniteLoadingHandler(stateChanger: StateChanger): Promise<void> {
            const response: ISearchResponse  = await ApiClient.post("/Api/User/Search", this.searchParameters);
            if(this.searchParameters.pageNumber === 1) {
                this.resultCount = String(response.count);
            }
            if (response.list.length) {
                // Use map to convert IUser data objects into actual instances of User class
                // (three dots is spread operator for concatenating arrays)
                this.userList.push(...response.list.map((u: IUser) => new User(u)));
                this.searchParameters.pageNumber += 1;
                stateChanger.loaded();
            }
            else {
                stateChanger.complete();
            }
        }

        changePassword(user: IUser): void {
            const dialogue = this.$refs.changePasswordDialogue as ChangePasswordDialogue;
            dialogue.changePassword(user);
        }

        sendReminderCheck(user: IUser): void {
            this.user.update(user);
            const dialogue = this.$refs.sendLinkDialogue as ConfirmDialogue;
            dialogue.show();
        }

        async sendReminder(): Promise<void> {
            const request = { email: this.user.email }
            const response = await ApiClient.post("/Api/Authentication/forgot", request, "send-reminder") as { isSuccess: boolean; message: string };
            if (response.isSuccess) {
                toastr.success(`${this.user.forename} ${this.user.surname} - ${this.user.email}`, "Sent");
                (this.$refs.sendLinkDialogue as ConfirmDialogue).hide();
            }
            else {
                // probably a dodgy domain?
                toastr.error("Please check the email address.", response.message); // could possibly use message box rather than toast?
            }
        }

    }
